import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ExternalLink } from 'lucide-react';
import TransactionOverlay from '../transactions/TransactionOverlay';
import { getIncomeByCustomerInvoiceDetail } from '../../utils/reportApi';
import { useInvoicePreviewModal } from '../../hooks/useInvoicePreviewModal';
import { DEFAULT_LOCALE, getCurrencyCode } from '../../utils/formatCurrency';
import type { IncomeByCustomerInvoiceDetailReport } from '../../types/report';

export interface IncomeByCustomerInvoiceDetailModalProps {
  invoiceId: number;
  onClose: () => void;
}

const formatAmount = (value: string) => {
  const amount = parseFloat(value);
  if (!Number.isFinite(amount)) return '0.00';
  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const incomeClassName = (value: string) =>
  parseFloat(value) < 0 ? 'text-red-600' : '';

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const IncomeByCustomerInvoiceDetailModal = ({
  invoiceId,
  onClose,
}: IncomeByCustomerInvoiceDetailModalProps) => {
  const [detail, setDetail] = useState<IncomeByCustomerInvoiceDetailReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { openInvoicePreview, invoicePreviewModal } = useInvoicePreviewModal();

  const loadDetail = useCallback(async () => {
    if (Number.isNaN(invoiceId)) {
      setError('Invalid invoice ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setDetail(await getIncomeByCustomerInvoiceDetail({ invoice_id: invoiceId }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoice detail');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  return (
    <>
    <TransactionOverlay
      variant="wide"
      title={detail?.invoice_number ?? 'Invoice detail'}
      subtitle={detail ? `Gross profit by line · ${detail.display_name}` : 'Loading…'}
      onClose={onClose}
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : detail ? (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm bg-slate-50 rounded-lg p-4">
            <div>
              <span className="text-slate-500 block text-xs uppercase tracking-wide">Customer</span>
              <span className="font-medium text-slate-900">{detail.display_name}</span>
            </div>
            <div>
              <span className="text-slate-500 block text-xs uppercase tracking-wide">Txn Date</span>
              <span>{formatDate(detail.txn_date)}</span>
            </div>
            <div>
              <span className="text-slate-500 block text-xs uppercase tracking-wide">Invoice</span>
              <span className="font-medium">{detail.invoice_number}</span>
            </div>
          </div>

          <div className="overflow-x-auto -mx-1">
            <p className="text-xs text-slate-500 text-right mb-2">
              Amounts in {getCurrencyCode()}
            </p>
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Item</th>
                  <th className="px-4 py-3 font-medium text-right">Qty</th>
                  <th className="px-4 py-3 font-medium text-right">Unit Price</th>
                  <th className="px-4 py-3 font-medium text-right">Unit Cost</th>
                  <th className="px-4 py-3 font-medium text-right">Revenue</th>
                  <th className="px-4 py-3 font-medium text-right">COGS</th>
                  <th className="px-4 py-3 font-medium text-right">Income</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {detail.lines.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                      No line items found.
                    </td>
                  </tr>
                ) : (
                  detail.lines.map((line) => (
                    <tr key={line.line_number} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-500">{line.line_number}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{line.item_name}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{line.qty}</td>
                      <td className="px-4 py-3 text-right text-slate-600">
                        {formatAmount(line.unit_price)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">
                        {formatAmount(line.unit_cost)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600 font-medium">
                        {formatAmount(line.revenue)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600 font-medium">
                        {formatAmount(line.cogs)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${incomeClassName(line.income)}`}>
                        {formatAmount(line.income)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={5} className="px-4 py-3 font-semibold text-slate-900 text-right">
                    Total
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">
                    {formatAmount(detail.totals.revenue)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">
                    {formatAmount(detail.totals.cogs)}
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-semibold text-slate-900 ${incomeClassName(detail.totals.income)}`}
                  >
                    {formatAmount(detail.totals.income)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => openInvoicePreview(invoiceId)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
            >
              <ExternalLink className="h-4 w-4" />
              View full invoice
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm ml-auto"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </TransactionOverlay>
    {invoicePreviewModal}
    </>
  );
};

export default IncomeByCustomerInvoiceDetailModal;
